/**
 * What undoing an upload would do, as the Equipment step's confirmation says it
 * (`equipment-page-undo-import`, R10, R12–R14). Pure: the server's preview and the page's own
 * placements in, one account out, so the hook and the modal cannot describe it differently.
 */
import type { TwinAssetImportUndoPreview, TwinAssetResponse } from '@repo/front-end-services';
import {
  registerCapacityLookup,
  removePlacementsFromLayout,
  resolvePlanCapacity,
} from '@repo/front-end-utils';
import type { FloorPlannerLayout } from '@repo/frontend-types';

/** One row the undo deletes, with every placement either side knows of. */
export interface UndoAddedRow {
  id: number;
  assetName: string | null;
  /** Placements on SAVED plans, from the server's preview. */
  serverPlacements: TwinAssetImportUndoPreview['added'][number]['placements'];
  /** The page's own placements (`coverage.placementsFor`), which see a plan not yet saved. */
  pageRefids: readonly string[];
}

export interface UndoAccount {
  added: UndoAddedRow[];
  /** The added rows on a plan, in either view (R10). */
  placed: UndoAddedRow[];
  /** The refids to take off the page's plan before the undo: its own, plus the server's for it. */
  placedRefids: string[];
  /** Placements on saved plans this step does not edit (R14), one entry per plan. */
  otherPlans: { planName: string; assets: number }[];
  restoredCount: number;
  /** Rows changed since the upload: the undo leaves their later edit (R12, *Left alone*). */
  editedSinceCount: number;
  /** Each restored row's capacity before the upload, where the upload changed it (R13). */
  capacityBefore: ReadonlyMap<number, unknown>;
}

export interface UndoPagePlacements {
  placementsFor: (assetId: number) => readonly string[];
  /** The saved plan this step edits; null when it has none. */
  pagePlanId: number | null;
}

/**
 * The upload's preview as one account (R10, R12). A row is placed when the server places it on a
 * saved plan OR the page's plan places it — the page sees placements not yet saved, the server sees
 * plans the page has not loaded. A server placement on a plan other than the page's is a refusal
 * (R14), never pruned.
 *
 * One import's change log holds one row per asset (`twin_asset_import_change`'s primary key is
 * `(import_id, twin_asset_id)`), so the preview names a row once: added, restored or edited since.
 */
export const undoAccount = (
  preview: TwinAssetImportUndoPreview,
  page: UndoPagePlacements,
): UndoAccount => {
  const added: UndoAddedRow[] = preview.added.map(row => ({
    id: row.id,
    assetName: row.assetName,
    serverPlacements: row.placements,
    pageRefids: page.placementsFor(row.id),
  }));
  const capacityBefore = new Map<number, unknown>();
  for (const row of preview.restored) {
    const capacity = row.fields.find(field => field.column === 'capacity');
    if (capacity) capacityBefore.set(row.id, capacity.before);
  }

  const onPagePlan = (planId: number) => page.pagePlanId !== null && planId === page.pagePlanId;
  const placed = added.filter(row => row.serverPlacements.length > 0 || row.pageRefids.length > 0);
  const refids = new Set<string>();
  const others = new Map<string, Set<number>>();
  for (const row of placed) {
    for (const refid of row.pageRefids) refids.add(refid);
    for (const spot of row.serverPlacements) {
      if (onPagePlan(spot.planId)) {
        refids.add(spot.refid);
      } else {
        const assets = others.get(spot.planName) ?? new Set<number>();
        assets.add(row.id);
        others.set(spot.planName, assets);
      }
    }
  }

  return {
    added,
    placed,
    placedRefids: Array.from(refids),
    otherPlans: Array.from(others, ([planName, assets]) => ({ planName, assets: assets.size })),
    restoredCount: preview.restored.length,
    editedSinceCount: preview.editedSince.length,
    capacityBefore,
  };
};

/** How the twin's calculated capacity moves, when it moves and can be said (R13). */
export type UndoCapacityChange = { kind: 'drop' | 'rise'; by: number } | null;

const numeric = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * The twin's resolved capacity now, against the same once the undo has run: the layout without the
 * placed refids, and the register without the added rows and with each restored row's capacity put
 * back (R13). Null — no sentence — when the two are equal, when either cannot be resolved, or when a
 * restored capacity is not a number: the sentence must never claim a change that will not happen.
 *
 * The pruned layout is a plain (pure) `removePlacementsFromLayout`, not a dry run: a dry run returns
 * the input layout as it is, which is the "before", not the "after".
 */
export const undoCapacityChange = (
  layout: FloorPlannerLayout | null | undefined,
  registerRows: readonly TwinAssetResponse[] | null,
  account: Pick<UndoAccount, 'added' | 'placedRefids' | 'capacityBefore'>,
): UndoCapacityChange => {
  if (!layout || !registerRows) return null;
  // `Array.from`: this app compiles to ES5, where `for…of` over a Map iterates nothing.
  if (!Array.from(account.capacityBefore.values()).every(numeric)) return null;
  const before = resolvePlanCapacity(layout, registerCapacityLookup(registerRows, true)).twin();

  const gone = new Set(account.added.map(row => row.id));
  const rowsAfter = registerRows
    .filter(row => !gone.has(row.id))
    .map(row =>
      account.capacityBefore.has(row.id)
        ? { ...row, capacity: account.capacityBefore.get(row.id) as number }
        : row,
    );
  const pruned = removePlacementsFromLayout(layout, new Set(account.placedRefids), {}).layout;
  const after = resolvePlanCapacity(pruned, registerCapacityLookup(rowsAfter, true)).twin();

  if (!numeric(before.value) || !numeric(after.value)) return null;
  if (after.value === before.value) return null;
  return after.value < before.value
    ? { kind: 'drop', by: before.value - after.value }
    : { kind: 'rise', by: after.value - before.value };
};
